import React from 'react';

interface DashboardTabProps {
  reportData?: any;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ reportData }) => {
  // Default complexity score and related data
  const complexityScore = 2.1;
  const complexityFactors = [
    "Pathogen complexity",
    "Resistance patterns"
  ];
  const complexityImplications = [
    "Treatment selection",
    "Monitoring requirements"
  ];

  // Resistance profile data
  const resistanceSummary = "This profile shows 1 resistance genes across 1 classes, indicating moderate antimicrobial resistance.";
  const resistanceImplications = [
    "May require combination therapy",
    "Consider alternative treatment options",
    "Monitor treatment response closely"
  ];

  // Primary treatment data
  const primaryTreatment = "Meropenem";
  const riskLevel = "Low-Medium Risk";
  const riskPercentage = 35;
  const commonAdverseReactions = [
    "reports",
    "reports",
    "reports"
  ];

  // Adverse reactions data
  const totalUniqueReactions = 13;
  
  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">Clinical Microbiology Report Dashboard</h2>
      
      <hr className="my-4 border-gray-200" />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Case Complexity Assessment */}
        <div className="border rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold mb-2">Case Complexity Assessment</h3>
          <p className="text-sm mb-3">Low Complexity: {complexityScore}/10</p>
          
          {/* Gauge Chart for Complexity */}
          <div className="mb-4">
            <svg width="200" height="100" viewBox="0 0 200 100">
              <path
                d="M20,80 A60,60 0 0,1 180,80"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="10"
                strokeLinecap="round"
              />
              <path
                d="M20,80 A60,60 0 0,1 90,27"
                fill="none"
                stroke="#84cc16"
                strokeWidth="10"
                strokeLinecap="round"
              />
              <circle cx="90" cy="27" r="8" fill="#4b5563" />
              {/* Ticks */}
              <text x="20" y="95" fontSize="10" textAnchor="middle">0</text>
              <text x="60" y="40" fontSize="10" textAnchor="middle">2.5</text>
              <text x="100" y="25" fontSize="10" textAnchor="middle">5</text>
              <text x="140" y="40" fontSize="10" textAnchor="middle">7.5</text>
              <text x="180" y="95" fontSize="10" textAnchor="middle">10</text>
            </svg>
          </div>
          
          <p className="text-sm mb-3">Standard case with common pathogens and clear treatment path</p>
          
          <div className="mb-2">
            <p className="font-medium text-sm">Factors:</p>
            <ul className="list-disc pl-5 text-sm">
              {complexityFactors.map((factor, index) => (
                <li key={index}>{factor}</li>
              ))}
            </ul>
          </div>
          
          <div>
            <p className="font-medium text-sm">Implications:</p>
            <ul className="list-disc pl-5 text-sm">
              {complexityImplications.map((implication, index) => (
                <li key={index}>{implication}</li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Resistance Profile */}
        <div className="border rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold mb-2">Resistance Profile</h3>
          
          {/* Bar Chart for Resistance */}
          <div className="h-48 mb-4">
            <svg width="100%" height="100%" viewBox="0 0 300 200">
              <g transform="translate(40, 20)">
                {/* Y-axis */}
                <line x1="0" y1="0" x2="0" y2="140" stroke="#e5e7eb" strokeWidth="2" />
                {/* X-axis */}
                <line x1="0" y1="140" x2="240" y2="140" stroke="#e5e7eb" strokeWidth="2" />
                
                {/* Y-axis ticks */}
                <text x="-5" y="140" fontSize="10" textAnchor="end">0</text>
                <text x="-5" y="70" fontSize="10" textAnchor="end">0.5</text>
                <text x="-5" y="0" fontSize="10" textAnchor="end">1</text>
                
                {/* Bar */}
                <rect x="100" y="0" width="40" height="140" fill="#c39bd3" />
                
                {/* X-axis label */}
                <text x="120" y="160" fontSize="10" textAnchor="middle">esbl</text>
                <text x="120" y="175" fontSize="10" textAnchor="middle">Resistance Class</text>
                
                {/* Y-axis label */}
                <text x="-30" y="70" fontSize="10" textAnchor="middle" transform="rotate(-90, -30, 70)">Count</text>
              </g>
            </svg>
          </div>
          
          <h4 className="font-medium text-sm mb-2">Resistance Summary:</h4>
          <p className="text-sm mb-3">{resistanceSummary}</p>
          
          <h4 className="font-medium text-sm mb-2">Implications:</h4>
          <ul className="list-disc pl-5 text-sm">
            {resistanceImplications.map((implication, index) => (
              <li key={index}>{implication}</li>
            ))}
          </ul>
        </div>
        
        {/* Meropenem Treatment */}
        <div className="border rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold mb-2">{primaryTreatment}</h3>
          <p className="text-sm mb-3">{riskLevel}</p>
          
          {/* Gauge Chart for Risk */}
          <div className="mb-4">
            <svg width="160" height="100" viewBox="0 0 160 100">
              <path
                d="M80,80 m-65,0 a65,65 0 1,1 130,0"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="10"
                strokeLinecap="round"
              />
              <path
                d="M80,80 m-65,0 a65,65 0 0,1 46,-62"
                fill="none"
                stroke="#84cc16"
                strokeWidth="10"
                strokeLinecap="round"
              />
              <text x="80" y="85" fontSize="12" fontWeight="bold" textAnchor="middle">{riskPercentage}%</text>
            </svg>
          </div>
          
          <h4 className="font-medium text-sm mb-2">Common Adverse Reactions:</h4>
          <ul className="list-disc pl-5 text-sm">
            {commonAdverseReactions.map((reaction, index) => (
              <li key={index}>{reaction}</li>
            ))}
          </ul>
        </div>
        
        {/* Adverse Reactions Profile */}
        <div className="border rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold mb-2">Adverse Reactions Profile</h3>
          
          {/* Pie Chart for Severity */}
          <div className="mb-4 flex justify-center">
            <svg width="160" height="100" viewBox="0 0 160 100">
              <circle cx="80" cy="50" r="45" fill="#84cc16" />
              <line x1="80" y1="50" x2="80" y2="5" stroke="#4b5563" strokeWidth="1" />
              <rect x="45" y="75" width="20" height="10" fill="#84cc16" />
              <text x="70" y="85" fontSize="10" textAnchor="start">Low Severity</text>
              <rect x="45" y="90" width="20" height="10" fill="#eab308" />
              <text x="70" y="100" fontSize="10" textAnchor="start">Medium Severity</text>
              <rect x="115" y="90" width="20" height="10" fill="#ef4444" />
              <text x="110" y="100" fontSize="10" textAnchor="end">High Severity</text>
            </svg>
          </div>
          
          <p className="text-sm text-center mb-4">Total unique adverse reactions: {totalUniqueReactions}</p>
          
          {/* View All Button */}
          <div className="border rounded p-2 text-sm cursor-pointer text-center flex items-center justify-between">
            <span>View All Adverse Reactions</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Treatment Comparison */}
      <div className="border rounded-lg p-4 shadow-sm mt-6">
        <h3 className="font-semibold mb-2">Treatment Comparison</h3>
        <p className="text-sm mb-4">Click on any treatment bar to view detailed adverse reactions</p>
        
        {/* Bar Chart for Treatment Comparison */}
        <div className="h-64">
          <svg width="100%" height="100%" viewBox="0 0 700 200">
            <g transform="translate(50, 20)">
              {/* Y-axis with grid lines */}
              <line x1="0" y1="0" x2="0" y2="140" stroke="#e5e7eb" strokeWidth="1.5" />
              {/* X-axis with grid lines */}
              <line x1="0" y1="140" x2="600" y2="140" stroke="#e5e7eb" strokeWidth="1.5" />
              
              {/* Horizontal grid lines */}
              <line x1="0" y1="0" x2="600" y2="0" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="3,3" />
              <line x1="0" y1="35" x2="600" y2="35" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="3,3" />
              <line x1="0" y1="70" x2="600" y2="70" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="3,3" />
              <line x1="0" y1="105" x2="600" y2="105" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="3,3" />
              
              {/* Better legend with borders */}
              <rect x="450" y="-15" width="150" height="60" rx="4" fill="white" stroke="#e5e7eb" strokeWidth="1" />
              <rect x="460" y="5" width="12" height="12" fill="#93c5fd" rx="2" />
              <text x="480" y="15" fontSize="11" alignmentBaseline="middle">Efficacy (%)</text>
              <rect x="460" y="25" width="12" height="12" fill="#4ade80" rx="2" />
              <text x="480" y="35" fontSize="11" alignmentBaseline="middle">Adverse Score (%)</text>
              
              {/* Treatment 1: Meropenem - Primary Treatment with highlight */}
              <g transform="translate(30, 0)" className="treatment-bar">
                <rect x="-15" y="0" width="80" height="140" fill="#f0f9ff" stroke="#93c5fd" strokeWidth="1" strokeDasharray="2,1" rx="4" />
                <rect x="0" y="40" width="22" height="100" fill="#93c5fd" rx="2" />
                <rect x="26" y="90" width="22" height="50" fill="#4ade80" rx="2" />
                <text x="24" y="160" fontSize="10" textAnchor="middle" fontWeight="bold">Meropenem</text>
                <text x="24" y="173" fontSize="8" textAnchor="middle">(Primary)</text>
              </g>
              
              {/* Treatment 2: Ertapenem */}
              <g transform="translate(100, 0)" className="treatment-bar">
                <rect x="0" y="45" width="22" height="95" fill="#93c5fd" rx="2" />
                <rect x="26" y="95" width="22" height="45" fill="#4ade80" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Ertapenem</text>
              </g>
              
              {/* Treatment 3: Imipenem */}
              <g transform="translate(170, 0)" className="treatment-bar">
                <rect x="0" y="50" width="22" height="90" fill="#93c5fd" rx="2" />
                <rect x="26" y="80" width="22" height="60" fill="#fcd34d" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Imipenem-Cilastatin</text>
              </g>
              
              {/* Treatment 4: Meropenem-Vaborbactam */}
              <g transform="translate(240, 0)" className="treatment-bar">
                <rect x="0" y="40" width="22" height="100" fill="#93c5fd" rx="2" />
                <rect x="26" y="110" width="22" height="30" fill="#4ade80" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Meropenem-Vaborbactam</text>
              </g>
              
              {/* Treatment 5: Cefoxitin */}
              <g transform="translate(310, 0)" className="treatment-bar">
                <rect x="0" y="60" width="22" height="80" fill="#93c5fd" rx="2" />
                <rect x="26" y="70" width="22" height="70" fill="#fcd34d" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Cefoxitin</text>
              </g>
              
              {/* Treatment 6: Plazomicin */}
              <g transform="translate(380, 0)" className="treatment-bar">
                <rect x="0" y="50" width="22" height="90" fill="#93c5fd" rx="2" />
                <rect x="26" y="45" width="22" height="95" fill="#ef4444" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Plazomicin</text>
              </g>
              
              {/* Treatment 7: Tigecycline */}
              <g transform="translate(450, 0)" className="treatment-bar">
                <rect x="0" y="70" width="22" height="70" fill="#93c5fd" rx="2" />
                <rect x="26" y="90" width="22" height="50" fill="#4ade80" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Tigecycline</text>
              </g>
              
              {/* Treatment 8: Fosfomycin */}
              <g transform="translate(520, 0)" className="treatment-bar">
                <rect x="0" y="65" width="22" height="75" fill="#93c5fd" rx="2" />
                <rect x="26" y="80" width="22" height="60" fill="#fcd34d" rx="2" />
                <text x="24" y="160" fontSize="9" textAnchor="middle">Fosfomycin</text>
              </g>
              
              {/* Y-axis labels with better formatting */}
              <text x="-10" y="140" fontSize="9" textAnchor="end" fontWeight="medium">0%</text>
              <text x="-10" y="105" fontSize="9" textAnchor="end" fontWeight="medium">25%</text>
              <text x="-10" y="70" fontSize="9" textAnchor="end" fontWeight="medium">50%</text>
              <text x="-10" y="35" fontSize="9" textAnchor="end" fontWeight="medium">75%</text>
              <text x="-10" y="0" fontSize="9" textAnchor="end" fontWeight="medium">100%</text>
              
              {/* X-axis label */}
              <text x="300" y="185" fontSize="11" textAnchor="middle" fontWeight="medium">Treatment Options</text>
              
              {/* Y-axis label */}
              <text x="-40" y="70" fontSize="11" textAnchor="middle" transform="rotate(-90, -40, 70)" fontWeight="medium">Percentage (%)</text>
            </g>
          </svg>
        </div>
        
        <div className="mt-4 mb-1 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-900">Primary treatment (Meropenem) is recommended with a higher efficacy score and balanced adverse reaction profile.</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab; 